"use client";

import { ChangeEvent, DragEvent, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { MediaFile } from "@/app/modules/media/api";
import { useMediaActions, useMediaFilesQuery, useMediaFoldersQuery } from "@/app/modules/media/hooks/useMediaQueries";
import { useWorkspace } from "@/app/components/WorkspaceShell";
import { Button } from "@/app/components/ui/Button";
import { Modal } from "@/app/components/Modal";

interface MediaImagePickerProps {
    open: boolean;
    workspaceUuid?: string;
    onClose: () => void;
    onSelect: (files: MediaFile[]) => void;
    multiple?: boolean;
    publiclyAccessible?: boolean;
    allowedMimeTypes?: readonly string[];
}

const imageFormatNames: Record<string, string> = {
    "image/jpeg": "JPG",
    "image/png": "PNG",
    "image/gif": "GIF",
    "image/webp": "WebP",
};

export default function MediaImagePicker({
    open,
    workspaceUuid: requestedWorkspaceUuid,
    onClose,
    onSelect,
    multiple = true,
    publiclyAccessible = false,
    allowedMimeTypes,
}: MediaImagePickerProps) {
    const t = useTranslations("common.mediaImagePicker");
    const tCommon = useTranslations("common");
    const { activeWorkspace } = useWorkspace();
    const [folderSelection, setFolderSelection] = useState<{ workspaceUuid: string; uuid: string } | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<MediaFile[]>([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const operationPending = useRef(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const workspaceUuid = requestedWorkspaceUuid ?? activeWorkspace?.uuid ?? "";
    const selectedFolderUuid = folderSelection?.workspaceUuid === workspaceUuid ? folderSelection.uuid : null;
    const foldersQuery = useMediaFoldersQuery(open ? workspaceUuid : "");
    const filesQuery = useMediaFilesQuery(open ? workspaceUuid : "", {
        folderUuid: selectedFolderUuid ?? undefined,
        type: "image",
        sortBy: "createdAt",
        sortDirection: "desc",
        page: 1,
        limit: 100,
    });
    const mediaActions = useMediaActions(workspaceUuid);
    const folders = foldersQuery.data ?? [];
    const queryFiles = filesQuery.data?.items ?? [];
    const files = [
        ...uploadedFiles.filter((uploadedFile) => !queryFiles.some((file) => file.uuid === uploadedFile.uuid)),
        ...queryFiles,
    ].filter((file) => !allowedMimeTypes || allowedMimeTypes.includes(file.mimeType));
    const supportedFormats = allowedMimeTypes?.map((mimeType) => imageFormatNames[mimeType] ?? mimeType).join(", ");
    const isBusy = uploadProgress !== null || isConfirming;
    const libraryError = filesQuery.isError || foldersQuery.isError;

    useEffect(() => {
        setFolderSelection(null);
    }, [workspaceUuid]);

    useEffect(() => {
        if (open) {
            setSelected(new Set());
            setUploadedFiles([]);
            setUploadError(null);
            setIsDragging(false);
        }
    }, [open, workspaceUuid]);

    function toggleFile(file: MediaFile) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(file.uuid)) {
                next.delete(file.uuid);
            } else {
                if (!multiple) next.clear();
                next.add(file.uuid);
            }
            return next;
        });
    }

    async function handleConfirm() {
        if (operationPending.current) return;
        const chosen = files.filter((f) => selected.has(f.uuid));
        if (chosen.length === 0) return;

        operationPending.current = true;
        setIsConfirming(true);
        setUploadError(null);

        try {
            const published = publiclyAccessible
                ? await Promise.all(chosen.map((file) => (
                    file.url.startsWith("/media-content/public/")
                        ? file
                        : mediaActions.publishFile(file.uuid) as Promise<MediaFile>
                )))
                : chosen;
            onSelect(published);
            onClose();
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : t("selectionFailed"));
        } finally {
            operationPending.current = false;
            setIsConfirming(false);
        }
    }

    async function uploadImage(file: File) {
        if (operationPending.current) return;
        if (!file.type.startsWith("image/")) {
            setUploadError(t("imageRequired"));
            return;
        }
        if (allowedMimeTypes && !allowedMimeTypes.includes(file.type)) {
            setUploadError(t("unsupportedFormat", { formats: supportedFormats ?? "" }));
            return;
        }

        operationPending.current = true;
        setUploadError(null);
        setUploadProgress(0);
        try {
            const uploaded = await mediaActions.uploadFileWithProgress(
                file,
                selectedFolderUuid,
                setUploadProgress,
                publiclyAccessible,
            ) as MediaFile;
            if (allowedMimeTypes && !allowedMimeTypes.includes(uploaded.mimeType)) {
                setUploadError(t("unsupportedFormat", { formats: supportedFormats ?? "" }));
                return;
            }
            if (uploaded.mimeType.startsWith("image/")) {
                setUploadedFiles((current) => [uploaded, ...current]);
                setSelected((current) => {
                    const next = new Set(current);
                    if (!multiple) next.clear();
                    next.add(uploaded.uuid);
                    return next;
                });
            }
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : t("uploadFailed"));
        } finally {
            operationPending.current = false;
            setUploadProgress(null);
        }
    }

    function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (file) {
            void uploadImage(file);
        }
        event.target.value = "";
    }

    function handleDrop(event: DragEvent<HTMLDivElement>) {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files.item(0);
        if (file) {
            void uploadImage(file);
        }
    }

    return (
        <Modal open={open} onClose={() => { if (!operationPending.current) onClose(); }} title={multiple ? t("titleMultiple") : t("titleSingle")} size="xl">
            <div className="flex flex-col md:flex-row gap-4 max-h-[70vh]">
                {/* Folders */}
                <div className="md:w-56 shrink-0 space-y-1 overflow-auto">
                    <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => setFolderSelection(null)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedFolderUuid === null
                                ? "bg-primary/15 text-primary font-medium"
                                : "text-text-secondary hover:bg-surface-hover hover:text-text"
                        }`}
                    >
                        {t("allFiles")}
                    </button>
                    {folders.map((folder) => (
                        <button
                            key={folder.uuid}
                            type="button"
                            disabled={isBusy}
                            onClick={() => setFolderSelection({ workspaceUuid, uuid: folder.uuid })}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                selectedFolderUuid === folder.uuid
                                    ? "bg-primary/15 text-primary font-medium"
                                    : "text-text-secondary hover:bg-surface-hover hover:text-text"
                            }`}
                        >
                            {folder.name}
                        </button>
                    ))}
                </div>

                {/* Images grid */}
                <div className="flex-1 overflow-auto">
                    <div
                        className={`mb-3 rounded-lg border border-dashed p-3 transition-colors ${
                            isDragging ? "border-primary bg-primary/10" : "border-border bg-bg-elevated"
                        }`}
                        onDragOver={(event) => {
                            event.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-xs font-medium text-text">{t("uploadTitle")}</p>
                                <p className="text-xs text-text-muted">{t("uploadDescription")}</p>
                                {supportedFormats ? <p className="mt-1 text-xs text-text-muted">{t("supportedFormats", { formats: supportedFormats })}</p> : null}
                            </div>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => inputRef.current?.click()}
                                disabled={isBusy}
                            >
                                {t("upload")}
                            </Button>
                            <input
                                ref={inputRef}
                                type="file"
                                accept={allowedMimeTypes?.join(",") ?? "image/*"}
                                className="hidden"
                                disabled={isBusy}
                                onChange={handleFileChange}
                            />
                        </div>
                        {uploadProgress !== null && (
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface" role="progressbar" aria-label={t("upload")} aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100}>
                                <div className="h-full bg-primary" style={{ width: `${uploadProgress}%` }} />
                            </div>
                        )}
                        {uploadError && <p className="mt-2 text-xs text-danger" role="alert">{uploadError}</p>}
                    </div>

                    {libraryError ? (
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-danger/20 bg-danger/5 p-3" role="alert">
                            <p className="text-sm text-danger">{t("loadFailed")}</p>
                            <Button type="button" variant="secondary" size="sm" onClick={() => { void filesQuery.refetch(); void foldersQuery.refetch(); }} disabled={isBusy || filesQuery.isFetching || foldersQuery.isFetching}>
                                {t("retry")}
                            </Button>
                        </div>
                    ) : null}
                    {filesQuery.isFetching && files.length === 0 ? (
                        <div className="flex h-48 items-center justify-center gap-2 text-text-muted text-sm" role="status">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
                            {t("loading")}
                        </div>
                    ) : files.length === 0 && !filesQuery.isError ? (
                        <div className="flex h-48 items-center justify-center text-text-muted text-sm">
                            {t("emptyFolder")}
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {files.map((file) => {
                                const isSelected = selected.has(file.uuid);
                                return (
                                    <button
                                        key={file.uuid}
                                        type="button"
                                        disabled={isBusy}
                                        aria-pressed={isSelected}
                                        onClick={() => toggleFile(file)}
                                        className={`relative aspect-square overflow-hidden rounded-lg border-2 transition ${
                                            isSelected
                                                ? "border-primary ring-2 ring-primary/20"
                                                : "border-border hover:border-primary/40"
                                        }`}
                                    >
                                        <Image
                                            src={file.thumbnailUrl ?? file.url}
                                            alt={file.originalName}
                                            fill
                                            sizes="(min-width: 640px) 25vw, 33vw"
                                            className="object-cover"
                                            loading="lazy"
                                            unoptimized
                                        />
                                        {isSelected && (
                                            <div className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white text-xs">
                                                ✓
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <span className="text-sm text-text-muted">
                    {t("selected", { count: selected.size })}
                </span>
                <div className="flex items-center gap-2">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isBusy}>
                        {tCommon("cancel")}
                    </Button>
                    <Button type="button" onClick={handleConfirm} disabled={selected.size === 0 || isBusy} isLoading={isConfirming}>
                        {t("assign")}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
